import { faker } from "@faker-js/faker";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import refreshIcon from "assets/svg/refresh-green.svg";
import HoverAuthorizeTooltip from "components/authorize/AuthorizeTooltip";
import { IOTPToolTip } from "components/common/Tooltip";
import DefaultModal from "components/modals/ModalTemplate";
import {
    ACTION_FAIL_MESSAGE,
    ACTUATOR_CONFIG_ACTIVATION_SUCCESS_MESSAGE,
    ACTUATOR_CONFIG_SUCCESS_MESSAGE,
} from "constant";
import { ACTIVATE, DEACTIVATE } from "constant/gateway";
import useCollectSort from "hooks/useCollectSort";
import { capitalize } from "lodash";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useButtonThrottle } from "hooks/common";
import { Button, Col, FormControl, InputGroup, Row } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import {
    getAllLastBusReading,
    getSensorLastReading,
    toggleActuatorActivation,
    updateActuatorCommand,
} from "service/gatewayService";
import { ActuatorCommandMode, LdsActuatorDetails } from "types/Actuator";
import { getAPIError, showErrorAlert, showSuccessAlert } from "utils/alert";
import { getCalendarFormat } from "utils/datetime";
import {
    formatValueWithUnit,
    getPrimaryUnit,
    getPrimaryUnitName,
    isFourWiredMotorControllerApp,
    isHttpSuccess,
} from "utils/functions";
import { formatReading, getActuatorState } from "utils/gatewayFunctions";

type SortableActuator = LdsActuatorDetails & {
    /** For sorting status */
    sortingStatus: string;
    /** For sorting mode */
    sortingMode: string;
};

type SortableActuatorPath =
    | keyof SortableActuator
    | `info.${keyof SortableActuator["info"]}`;

/** CLS = 32769 is Analog IO controller, otherwise digital IO controller */
const isAnalog = (CLS: string) => CLS === "32769";

const sortStatusColumn = (actuator: LdsActuatorDetails) => {
    // For asc: -, 0..10, active, inactive, offline
    // For desc: offline, inactive, active, 10..0, -
    let sortingStatus = "";

    const { timestamp, status, said, CLS } = actuator;

    if (actuator.info.mode === ActuatorCommandMode.MOTOR) {
        // Order 1
        sortingStatus = "1";
    } else if (status !== null && status !== undefined) {
        const paddedTimestamp = (timestamp ?? 0).toString().padStart(16, "0");
        const statusValue = status[said] ?? 0;
        if (isAnalog(CLS)) {
            // Order 2
            // Analog type, status shown as 0.00 /1.00
            sortingStatus = `2_${statusValue.toString()}_${paddedTimestamp}`;
        } else {
            // Order 3
            // status shown as active (1) / inactive (0)
            // For asc: active (1) before inactive (0)
            // For desc: inactive (0) before active (1)
            sortingStatus = `${statusValue === 1 ? "3" : "4"
                }_${paddedTimestamp}`;
        }
    } else {
        // Order 4
        // For actuators without status (offline), sort them last
        sortingStatus =
            "5_" + (actuator.timestamp || 0).toString().padStart(16, "0");
    }

    return sortingStatus;
};

function transformToSortableActuator(
    actuator: LdsActuatorDetails
): SortableActuator {
    // Status Column Sorting Logic

    const sortingStatus = sortStatusColumn(actuator);
    const sortingMode = getMode(actuator);

    return {
        ...actuator,
        sortingStatus,
        sortingMode,
    };
}

type ActuatorListHeadersProps = Readonly<{
    isRefreshing: boolean;
    refreshAllStatus: () => Promise<void>;
    renderSortIcon: (key: SortableActuatorPath) => JSX.Element;
}>;

function ActuatorListHeaders(props: ActuatorListHeadersProps): JSX.Element {
    const { isRefreshing, refreshAllStatus, renderSortIcon } = props;
    return (
        <div className="table-head">
            <Row className="no-checkbox">
                <Col sm={1} md={1}>
                    Actuator Name {renderSortIcon("name")}
                </Col>
                <Col sm={1} md={1}>
                    Type {renderSortIcon("type")}
                </Col>
                <Col sm={1} md={1}>
                    Control {renderSortIcon("info.auto")}
                </Col>
                <Col sm={1} md={1}>
                    Mode {renderSortIcon("sortingMode")}
                </Col>
                <Col sm={2} md={2}>
                    Status
                    <IconButton className="mr-2" onClick={refreshAllStatus}>
                        <img
                            className={
                                isRefreshing
                                    ? "bigRefresh refresh-animation"
                                    : "bigRefresh"
                            }
                            src={refreshIcon}
                            alt="refresh"
                        />
                    </IconButton>
                    {renderSortIcon("sortingStatus")}
                </Col>
                <Col md={2}>
                    Last Activated {renderSortIcon("info.activate_ts")}
                </Col>

                <Col sm={2} md={2}>
                    Action
                </Col>
            </Row>
        </div>
    );
}

const getMode = (actuator: LdsActuatorDetails) => {
    switch (actuator.info.mode) {
        case ActuatorCommandMode.LOOP:
            return capitalize(ActuatorCommandMode.LOOP);
        case ActuatorCommandMode.LEVEL:
            if (
                actuator.APP &&
                ["8192"].includes(actuator.APP[actuator.said].APPID)
            ) {
                return "-";
            }
            return capitalize(ActuatorCommandMode.LEVEL);
        case ActuatorCommandMode.PULSE:
            return capitalize(ActuatorCommandMode.PULSE);
        case ActuatorCommandMode.PWM:
            return ActuatorCommandMode.PWM;
        case ActuatorCommandMode.ANALOG:
        case ActuatorCommandMode.MOTOR:
            return "-";
    }
    return "";
};

export type ActuatorListProps = {
    gatewayName?: string;
    actuators: LdsActuatorDetails[];
};
const ActuatorList = ({ actuators, gatewayName }: ActuatorListProps) => {
    const params: any = useParams();
    const [modalShow, setModalShow] = useState(false);
    const [actuatorToOverride, setActuatorToOverride]: any = useState({});
    const { throttle, isThrottled } = useButtonThrottle(2000);

    const [actuatorList, setActuatorList] = useState<LdsActuatorDetails[]>([]);
    const [initialActuatorList, setInitialActuatorList] = useState<
        LdsActuatorDetails[]
    >([]);

    const [isRefreshing, setIsRefreshing] = useState(false);
    const { data: actuatorCollect, sortIcon } = useCollectSort(
        actuatorList,
        transformToSortableActuator,
        { resetOnCollectionChange: false }
    );

    useEffect(() => {
        setActuatorList(actuators);
        setInitialActuatorList(actuators);
    }, [actuators]);

    const renderSortIcon = useCallback(
        (key: SortableActuatorPath) => sortIcon(key.toString()),
        [sortIcon]
    );

    const formatDateTime = (date: number, isLowerCase = false) => {
        const timeString = moment(date, "X").calendar(getCalendarFormat());

        if (isLowerCase) {
            return timeString[0].toLowerCase() + timeString.slice(1);
        }

        return timeString;
    };

    const handleControlButtonClick = (
        action: string,
        actuator: LdsActuatorDetails,
        polaris?: boolean
    ) => {
        throttle(`${actuator.said}-${action}`, () => {
            switch (action) {
                case "activate":
                    toggleActivation(actuator, ACTIVATE);
                    break;
                case "deactivate":
                    toggleActivation(actuator, DEACTIVATE);
                    break;
                case "forward":
                    toggleActivation(actuator, ACTIVATE, true);
                    break;
                case "reverse":
                    toggleActivation(actuator, ACTIVATE, false);
                    break;
                case "pause":
                    toggleActivation(actuator, DEACTIVATE);
                    break;
                default:
                    break;
            }
        });
    };

    const showOverrideConfirmation = (actuator: LdsActuatorDetails) => {
        setModalShow(true);
        setActuatorToOverride(actuator);
    };

    const renderControl = (a: SortableActuator) => {
        return a.info.auto ? "Auto" : "Manual";
    };

    const renderMode = (a: SortableActuator) => {
        return getMode(a);
    };

    const renderStatus = (a: SortableActuator) => {
        const { timestamp, status, said, APP, CLS } = a;

        const renderHighLow = () => {
            if (isAnalog(CLS)) {
                const unit = getPrimaryUnit(APP?.[said]);
                const defaultUnitObj: any = getPrimaryUnitName(APP?.[said]);

                return (
                    <span className="reading">
                        {formatValueWithUnit(
                            formatReading(
                                status[said],
                                defaultUnitObj.ACCURACY
                            ),
                            unit
                        )}
                    </span>
                );
            }
            switch (status[said]) {
                case 1:
                    return (
                        <>
                            <i className="material-icons circle online">
                                circle
                            </i>
                            <span className="reading">Active</span>
                        </>
                    );
                case 0:
                    return (
                        <>
                            <i className="material-icons circle offline">
                                circle
                            </i>
                            <span className="reading">Inactive</span>
                        </>
                    );
                default:
                    if (APP) {
                        return (
                            <span className="reading">
                                {" "}
                                {status[said]} {getPrimaryUnit(APP[said])}
                            </span>
                        );
                    }
            }
        };

        if (timestamp) {
            const formattedTimestamp = formatDateTime(timestamp, true);
            return (
                <>
                    {renderHighLow()}
                    <IOTPToolTip message={formattedTimestamp}>
                        <Typography
                            noWrap
                            className="ml-2"
                        >{` ${formattedTimestamp}`}</Typography>
                    </IOTPToolTip>
                </>
            );
        } else {
            return <>Offline</>;
        }
    };

    const renderLastActivated = (a: SortableActuator) => {
        const lastActivated = a.info.activate_ts;
        if (!lastActivated) {
            return (
                <Col md={{ span: 2, order: "6" }} xs={{ span: 11, order: "7" }}>
                    -
                </Col>
            );
        }

        const formattedTimestamp = formatDateTime(lastActivated);

        return (
            <Col
                md={{ span: 2, order: "6" }}
                xs={{ span: 11, order: "7" }}
                className="d-flex align-items-center"
            >
                <IOTPToolTip message={formattedTimestamp}>
                    <Typography noWrap>{formattedTimestamp}</Typography>
                </IOTPToolTip>
            </Col>
        );
    };

    const renderButtons = (a: SortableActuator, isFourWired: boolean) => {
        return (
            <Col md={{ span: 3, order: "7" }} xs={{ span: 11, order: "8" }}>
                <div className="d-flex w-100 flex-wrap">
                    {a.info.auto && (
                        <HoverAuthorizeTooltip permission="gateway:update">
                            <Button
                                className="btn-override"
                                onClick={() => {
                                    showOverrideConfirmation(a);
                                }}
                            >
                                Override
                            </Button>
                        </HoverAuthorizeTooltip>
                    )}
                    {/* Four wire motor controller */}
                    {isFourWired && (
                        <>
                            <HoverAuthorizeTooltip permission="gateway:update">
                                <Button
                                    className="btn-forward"
                                    variant="primary"
                                    disabled={
                                        a.info.auto ||
                                        isThrottled(`${a.said}-forward`)
                                    }
                                    onClick={() =>
                                        handleControlButtonClick(
                                            "forward",
                                            a,
                                            true
                                        )
                                    }
                                >
                                    Forward
                                </Button>
                            </HoverAuthorizeTooltip>
                            <HoverAuthorizeTooltip permission="gateway:update">
                                <Button
                                    className="btn-reverse"
                                    variant="danger"
                                    disabled={
                                        a.info.auto ||
                                        isThrottled(`${a.said}-reverse`)
                                    }
                                    onClick={() =>
                                        handleControlButtonClick(
                                            "reverse",
                                            a,
                                            false
                                        )
                                    }
                                >
                                    Reverse
                                </Button>
                            </HoverAuthorizeTooltip>
                            <HoverAuthorizeTooltip permission="gateway:update">
                                <Button
                                    className="btn-pause"
                                    disabled={
                                        a.info.auto ||
                                        isThrottled(`${a.said}-pause`)
                                    }
                                    onClick={() =>
                                        handleControlButtonClick("pause", a)
                                    }
                                >
                                    Pause
                                </Button>
                            </HoverAuthorizeTooltip>
                        </>
                    )}

                    {/* Non-four wire motor controller */}
                    {!isFourWired && (
                        <>
                            <HoverAuthorizeTooltip permission="gateway:update">
                                <Button
                                    className="btn-activate"
                                    variant="primary"
                                    disabled={
                                        a.info.auto ||
                                        isThrottled(`${a.said}-activate`)
                                    }
                                    onClick={() =>
                                        handleControlButtonClick("activate", a)
                                    }
                                >
                                    Activate
                                </Button>
                            </HoverAuthorizeTooltip>
                            <HoverAuthorizeTooltip permission="gateway:update">
                                <Button
                                    className="btn-deactivate"
                                    variant="danger"
                                    disabled={
                                        a.info.auto ||
                                        isThrottled(`${a.said}-deactivate`)
                                    }
                                    onClick={() =>
                                        handleControlButtonClick(
                                            "deactivate",
                                            a
                                        )
                                    }
                                >
                                    Deactivate
                                </Button>
                            </HoverAuthorizeTooltip>
                            <HoverAuthorizeTooltip permission="gateway:update">
                                <Button
                                    className="btn btn-abort"
                                    hidden={
                                        a.CLS === "32769" ||
                                        a.info.mode ===
                                        ActuatorCommandMode.LEVEL
                                    }
                                    disabled={a.info.auto}
                                    onClick={() => {
                                        abort(a);
                                    }}
                                >
                                    Abort
                                </Button>
                            </HoverAuthorizeTooltip>
                        </>
                    )}
                </div>
            </Col>
        );
    };

    const renderActuators = () => {
        return actuatorCollect.map((a: SortableActuator, index: number) => {
            const { type, name } = a;
            const isFourWired = isFourWiredMotorControllerApp(
                a.OBJ,
                a.appId ?? ""
            );

            return (
                <div
                    className="table-row auto-height-row"
                    key={faker.datatype.uuid()}
                >
                    <Row className="no-checkbox">
                        <Col
                            md={{ span: 1, order: "1" }}
                            xs={{ span: 11, order: "1" }}
                            className="d-flex align-items-center"
                        >
                            <IOTPToolTip
                                message={a.name}
                                preserveWhitespace={true}
                            >
                                <Typography
                                    noWrap
                                    className="preserve-whitespace"
                                >
                                    <Link
                                        to={{
                                            pathname: `/actuator-details/${params.gatewayId}/${params.busNumber}/${a.UID}/${a.said}`,
                                            state: {
                                                type: type,
                                                gatewayName: gatewayName,
                                                name: name,
                                                isFourWired,
                                            },
                                        }}
                                        className="preserve-whitespace"
                                    >
                                        {a.name}
                                    </Link>
                                </Typography>
                            </IOTPToolTip>
                        </Col>
                        <Col
                            md={{ span: 1, order: "2" }}
                            xs={{ span: 11, order: "3" }}
                            className="d-flex align-items-center"
                        >
                            <IOTPToolTip message={a.type}>
                                <Typography noWrap>{a.type}</Typography>
                            </IOTPToolTip>
                        </Col>
                        <Col
                            md={{ span: 1, order: "3" }}
                            xs={{ span: 11, order: "4" }}
                            className="d-flex align-items-center"
                        >
                            {renderControl(a)}
                        </Col>
                        <Col
                            md={{ span: 1, order: "4" }}
                            xs={{ span: 11, order: "5" }}
                            className="d-flex align-items-center"
                        >
                            {renderMode(a)}
                        </Col>
                        <Col
                            md={{ span: 2, order: "5" }}
                            xs={{ span: 11, order: "6" }}
                            className="d-flex align-items-center flex-wrap"
                        >
                            {a.info.mode !== ActuatorCommandMode.MOTOR && (
                                <>
                                    {renderStatus(a)}
                                    <IconButton
                                        className="ml-2 mr-2"
                                        onClick={(e) => {
                                            handleRefreshStatus(a, e);
                                        }}
                                    >
                                        <img
                                            className="refresh-actuator"
                                            src={refreshIcon}
                                            alt="refresh"
                                        />
                                    </IconButton>
                                </>
                            )}
                            {a.info.mode === ActuatorCommandMode.MOTOR && "-"}
                        </Col>
                        {renderLastActivated(a)}

                        {renderButtons(a, isFourWired)}
                        <Col
                            md={{ span: 1, order: "last" }}
                            xs={{ span: 1, order: "2" }}
                            className="d-flex align-items-center justify-content-end"
                        >
                            <Link
                                to={{
                                    pathname: `/actuator-details/${params.gatewayId}/${params.busNumber}/${a.UID}/${a.said}`,
                                    state: {
                                        type: type,
                                        gatewayName: gatewayName,
                                        name: name,
                                        isFourWired,
                                    },
                                }}
                            >
                                <i className="material-icons right">
                                    keyboard_arrow_right
                                </i>
                            </Link>
                        </Col>
                    </Row>
                </div>
            );
        });
    };

    const abort = async (actuator: any) => {
        const { said, UID: ldsuId } = actuator;

        const abortRes = await toggleActuatorActivation({
            gatewayId: params.gatewayId,
            deviceId: ldsuId,
            said,
            activate: false,
            immediately: true,
        });

        if (isHttpSuccess(abortRes.status)) {
            const updatedList = actuatorList.map((a: LdsActuatorDetails) => {
                if (a.UID === ldsuId && a.said === said) {
                    return { ...a, info: abortRes.data };
                }
                return a;
            });

            setActuatorList(updatedList);
            showSuccessAlert({
                message: ACTUATOR_CONFIG_ACTIVATION_SUCCESS_MESSAGE.fill({
                    state: "aborted",
                }),
            });
        } else {
            showErrorAlert(getAPIError(abortRes, ACTION_FAIL_MESSAGE));
        }
    };

    const toggleActivation = async (
        actuator: any,
        activation_state: string,
        polarity?: boolean
    ) => {
        const { said, UID: ldsuId } = actuator;
        const isFourWired = isFourWiredMotorControllerApp(
            actuator.OBJ,
            actuator.appId
        );

        const payload: any = {
            gatewayId: params.gatewayId,
            deviceId: ldsuId,
            said,
            activate: activation_state === ACTIVATE,
        };

        if (isFourWired && polarity !== undefined) {
            payload.polarity = polarity;
        }

        const res = await toggleActuatorActivation(payload);

        if (isHttpSuccess(res.status)) {
            if (activation_state === ACTIVATE) {
                const { activate_ts } = res.data;
                const updatedList = actuatorList.map(
                    (a: LdsActuatorDetails) => {
                        if (a.UID === ldsuId && a.said === said) {
                            const info = {
                                ...a.info,
                                activate_ts,
                            };

                            return { ...a, info };
                        }
                        return a;
                    }
                );

                setActuatorList(updatedList);
            }

            const newState = getActuatorState({
                isFourWired,
                activation_state,
                polarity: polarity ?? false,
            });

            let successMessage;
            if (isFourWired) {
                if (activation_state === DEACTIVATE) {
                    successMessage = "Motor movement paused.";
                } else {
                    successMessage = `${newState} action activated successfully.`;
                }
            } else {
                successMessage =
                    ACTUATOR_CONFIG_ACTIVATION_SUCCESS_MESSAGE.fill({
                        state: newState.toLowerCase(),
                    });
            }

            showSuccessAlert({
                message: successMessage,
            });
        } else {
            showErrorAlert(getAPIError(res, ACTION_FAIL_MESSAGE));
        }
    };

    const handleRefreshStatus = (
        actuator: SortableActuator,
        e: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        throttle(`refresh-${actuator.said}`, () => {
            const target = e.currentTarget;
            const imgEl = target.querySelector(".refresh-actuator");

            if (imgEl) {
                imgEl.classList.add("refresh-state");
            }

            refreshOneStatus(actuator).finally(() => {
                if (imgEl) {
                    imgEl.classList.remove("refresh-state");
                }
            });
        });
    };

    const refreshOneStatus = async (a: SortableActuator) => {
        const { UID, said } = a;

        const refreshOneStatusRes: any = await getSensorLastReading(
            params.gatewayId,
            UID
        );

        if (isHttpSuccess(refreshOneStatusRes.status)) {
            const { value, ts } = refreshOneStatusRes.data;

            const updateList = (list: LdsActuatorDetails[]) =>
                list.map((s: LdsActuatorDetails) => {
                    if (s.UID === UID && s.said === said) {
                        return { ...s, status: value, timestamp: ts };
                    }
                    return s;
                });

            setActuatorList(updateList(actuatorList));
            setInitialActuatorList(updateList(initialActuatorList));
        }
    };

    const refreshAllStatus = async () => {
        setIsRefreshing(true);
        const refreshAllStatusRes = await getAllLastBusReading(
            params.gatewayId,
            params.busNumber
        );
        if (isHttpSuccess(refreshAllStatusRes.status)) {
            const {
                data: { devices: allStatus },
            } = refreshAllStatusRes;

            const actuatorsWithStatus: LdsActuatorDetails[] = [];

            for (const actuator of actuatorList) {
                for (const t of allStatus) {
                    if (actuator.UID === t.UID) {
                        actuatorsWithStatus.push({
                            ...actuator,
                            status: t.value,
                            timestamp: t.ts,
                        });
                    }
                }
            }

            setActuatorList(actuatorsWithStatus);
            setIsRefreshing(false);
        }
        setIsRefreshing(false);
    };

    const override = async () => {
        const { UID, said, info, name, OBJ, appId } = actuatorToOverride;
        const isFourWired = isFourWiredMotorControllerApp(OBJ, appId);

        const body = {
            ...info,
            name,
            auto: false,
        };

        if (isFourWired) {
            body.mode = ActuatorCommandMode.MOTOR;
        }

        delete body.activate_ts;
        delete body.modify_ts;

        const overrideRes: any = await updateActuatorCommand(
            params.gatewayId,
            UID,
            said,
            body
        );

        if (isHttpSuccess(overrideRes.status)) {
            const updatedList = actuatorList.map((a: LdsActuatorDetails) => {
                if (a.UID === UID && a.said === said) {
                    const info = { ...a.info, ...body };

                    return { ...a, info };
                }
                return a;
            });

            setActuatorList(updatedList);
            showSuccessAlert({
                message: ACTUATOR_CONFIG_SUCCESS_MESSAGE,
            });
        } else {
            showErrorAlert(getAPIError(overrideRes, ACTION_FAIL_MESSAGE));
        }

        setModalShow(false);
    };

    const searchActuator = (query: string) => {
        if (query !== "") {
            const filtered: any = [];
            initialActuatorList.forEach((i: LdsActuatorDetails) => {
                if (i.name.toLowerCase().includes(query.toLowerCase())) {
                    filtered.push(i);
                }
            });
            setActuatorList(filtered);
        } else {
            setActuatorList(initialActuatorList);
        }
    };

    return (
        <>
            <Row className="mt-4">
                <Col className="table-head-options">
                    <div className="search float-right">
                        <InputGroup>
                            <FormControl
                                type="text"
                                placeholder="Search.."
                                aria-describedby="button-addon2"
                                onChange={(e: any) => {
                                    searchActuator(e.target.value);
                                }}
                            />
                        </InputGroup>
                    </div>
                </Col>
            </Row>
            <Row className="cstm-table mt-4">
                <Col sm={12}>
                    <ActuatorListHeaders
                        isRefreshing={isRefreshing}
                        refreshAllStatus={refreshAllStatus}
                        renderSortIcon={renderSortIcon}
                    />

                    {actuatorList.length > 0 ? (
                        renderActuators()
                    ) : (
                        <div className="mt-5 text-center">
                            No Actuators to display
                        </div>
                    )}
                </Col>
            </Row>
            <DefaultModal
                modalShow={modalShow}
                setModalShow={() => setModalShow(false)}
                modalType="cancelConfirm"
                modalIcon="help_outline"
                modalTitle="Confirmation"
                modalContent={
                    <>
                        Override will change the control from AUTO to
                        MANUAL.<br></br>Do you want to Override?
                    </>
                }
                resendFunction={override}
            />
        </>
    );
};

export default ActuatorList;
